/* Suspend only the covered mobile collection grid; preserve its exact height. */
function createVianelliCollectionGridBackground(environment) {
  const { window, document } = environment;
  const media = window.matchMedia('(max-width: 1024px)');
  const owned = new Map();
  let active = false, frozenHeight = 0, current = null, prepared = null;
  const properties = ['height', 'box-sizing', 'content-visibility'];
  const eligible = () => document.body?.classList.contains('vianelli-collections') && media.matches &&
    window.CSS?.supports('content-visibility', 'hidden');
  const isGrid = node => node?.localName === 'ul' && node.id === 'product-grid' &&
    !node.querySelector('#FacetFiltersFormMobile, .mobile-facets');
  const restore = node => {
    const entries = owned.get(node);
    if (!entries) return;
    owned.delete(node);
    for (const entry of entries) {
      const style = node.style;
      if (style.getPropertyValue(entry.property) !== entry.appliedValue || style.getPropertyPriority(entry.property) !== entry.appliedPriority) continue;
      if (entry.previousValue) style.setProperty(entry.property, entry.previousValue, entry.previousPriority);
      else style.removeProperty(entry.property);
    }
  };
  const apply = node => {
    if (!isGrid(node) || owned.has(node)) return owned.has(node);
    const entries = properties.map(property => ({ property,
      previousValue: node.style.getPropertyValue(property),
      previousPriority: node.style.getPropertyPriority(property),
    }));
    const values = [`${frozenHeight}px`, 'border-box', 'hidden'];
    for (let index = 0; index < entries.length; index++) {
      const entry = entries[index];
      node.style.setProperty(entry.property, values[index], 'important');
      entry.appliedValue = node.style.getPropertyValue(entry.property);
      entry.appliedPriority = node.style.getPropertyPriority(entry.property);
    }
    owned.set(node, entries);
    return true;
  };
  const resume = () => {
    const previous = active;
    active = false;
    for (const node of [...owned.keys()]) restore(node);
    frozenHeight = 0; current = null; prepared = null;
    return previous;
  };
  const suspend = () => {
    if (active) return true;
    if (!eligible()) return false;
    const grid = document.getElementById('product-grid');
    if (!isGrid(grid) || !grid.isConnected) return false;
    // Read the painted grid before the early drawer mutates its disclosure.
    // Reserve that exact border-box height; no estimate and no auto skipping.
    const bounds = grid.getBoundingClientRect();
    if (!Number.isFinite(bounds.height) || bounds.height < 0 || bounds.width <= 0) return false;
    frozenHeight = bounds.height;
    if (!apply(grid)) return false;
    current = grid; active = true;
    return true;
  };
  const prepare = nextGrid => {
    if (!active || !eligible() || !isGrid(nextGrid)) return false;
    if (prepared && prepared !== current && prepared !== nextGrid) restore(prepared);
    if (!apply(nextGrid)) return false;
    prepared = nextGrid;
    return true;
  };
  const commit = nextGrid => {
    if (!active || !eligible()) { resume(); return false; }
    if (!isGrid(nextGrid) || !nextGrid.isConnected || document.getElementById('product-grid') !== nextGrid || !owned.has(nextGrid)) return false;
    current = nextGrid; prepared = null;
    // An old UL may enter the bounded rendered cache. Restore it only after
    // detachment, so neither cached responses nor cached cards retain styles.
    for (const node of [...owned.keys()]) if (node !== nextGrid && !node.isConnected) restore(node);
    return true;
  };
  const cancelPrepared = nextGrid => {
    if (nextGrid && nextGrid !== current && !nextGrid.isConnected) restore(nextGrid);
    if (prepared === nextGrid) prepared = null;
  };
  const releaseDetached = node => {
    if (!node || node.isConnected) return false;
    restore(node);
    if (current === node) current = null;
    if (prepared === node) prepared = null;
    return true;
  };
  const onMediaChange = event => { if (!event.matches) resume(); };
  media.addEventListener('change', onMediaChange);
  return { suspend, resume, prepare, commit, cancelPrepared, releaseDetached,
    isSuspended: () => active,
    // Diagnostic only; does not read layout or expose/mutate card content.
    snapshot: () => ({ active, frozenHeight, ownedNodes: owned.size, currentConnected: !!current?.isConnected, preparedConnected: !!prepared?.isConnected }),
    destroy() { resume(); media.removeEventListener('change', onMediaChange); },
  };
}
if (typeof module !== 'undefined' && module.exports) module.exports = createVianelliCollectionGridBackground;

(() => {
  if (document.body && !document.body.classList.contains('vianelli-collections')) return;
  window.VianelliCollectionGridBackground ||= createVianelliCollectionGridBackground({ window, document });
  // The bridge remains usable before this optional deferred asset arrives.
  // Joining an already-open modal cannot flash products: the panel covers it.
  if (document.getElementById('FacetFiltersFormMobile')?.closest('details')?.open) window.VianelliCollectionGridBackground.suspend();
})();
